import type { SwitchableContext } from '../features/team/api-client'

/**
 * The active workspace resolved against the caller's switchable contexts —
 * the single shape billing + team pages branch on. Derived purely from the
 * selected context key + the contexts list, so a page never re-derives
 * personal/owner/member from `/organizations/me`'s static role.
 *
 * - `personal` — acting in the owned org, personal mode (full personal billing,
 *   no in-app team management).
 * - `team-owner` — acting in an org in team mode as its owner (team billing +
 *   management).
 * - `team-member` — holding a seat in an org (read-only billing, redirected
 *   away from the team page).
 * - `unknown` — contexts not loaded yet or the active key isn't in the list
 *   (e.g. a stale localStorage key); treat as personal-safe until resolved.
 */
export type ResolvedContext =
  | { kind: 'personal' }
  | { kind: 'team-owner'; organizationId: number; orgName: string }
  | { kind: 'team-member'; organizationId: number; orgName: string }
  | { kind: 'unknown' }

export function resolveActiveContext(
  contexts: SwitchableContext[] | null,
  activeContextKey: string
): ResolvedContext {
  if (!contexts) return { kind: 'unknown' }
  const active = contexts.find((c) => c.key === activeContextKey)
  if (!active) return { kind: 'unknown' }
  if (active.mode === 'personal') return { kind: 'personal' }
  return {
    kind: active.role === 'owner' ? 'team-owner' : 'team-member',
    organizationId: active.organizationId,
    orgName: active.name,
  }
}
